import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import ForceGraph2D from "react-force-graph-2d";
import NodeTooltip from "./NodeTooltip";
import { useMaterialUIController } from "context";
import { IconButton } from "@material-ui/core";
import { Box } from "@mui/material";

export default function GraphView({ graphData, searchQuery, focusNodeId }) {
  const fgRef = useRef(null);
  const containerRef = useRef(null);
  const [hoverNode, setHoverNode] = useState(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const getColor = (node) => {
    if (node.severity === 0) return "#4caf50";
    if (node.severity === 1) return "#f44336";
    if (node.severity >= 2) return "#f44336";
    return node.color || "#9e9e9e";
  };

  const matchedNodes = useMemo(() => {
    if (!searchQuery) return [];
    return graphData?.nodes.filter(
      (n) =>
        n.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (n.name && n.name.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [searchQuery, graphData]);

  const centerGraph = useCallback(
    (node = null, zoom = 2.32, duration = 500) => {
      if (!fgRef.current) return;
      const x = node?.x || 0;
      const y = node?.y || 0;
      fgRef.current.centerAt(x, y, duration);
      fgRef.current.zoom(zoom, duration);
    },
    []
  );

  // Center graph on mount or graph data change
  useEffect(() => {
    if (!graphData || !fgRef.current) return;
    centerGraph();
  }, [graphData, centerGraph]);

  // Center graph on focusNodeId or search match
  useEffect(() => {
    if (!graphData || !fgRef.current) return;

    if (focusNodeId) {
      const node = graphData.nodes.find((n) => n.id === focusNodeId);
      if (node) centerGraph(node, 2.32, 1000);
    } else if (matchedNodes.length > 0) {
      centerGraph(matchedNodes[0], 2.32, 1000);
    } else {
      centerGraph();
    }
  }, [focusNodeId, matchedNodes, graphData, centerGraph]);

  // Resize observer to dynamically update graph size
  useEffect(() => {
    if (!containerRef.current) return;

    const handleResize = () => {
      if (!fgRef.current || !containerRef.current) return;
      fgRef.current.width = containerRef.current.clientWidth;
      fgRef.current.height = containerRef.current.clientHeight;
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

    useEffect(() => {
      if (!fgRef.current) return;
      // More negative charge = more repulsion
      fgRef.current.d3Force("charge").strength(-100);
      // Increase link distance
      fgRef.current.d3Force("link").distance(50);
    }, [graphData]);


  const handleZoomIn = () => {
    if (!fgRef.current) return;
    fgRef.current.zoom(fgRef.current.zoom() * 1.2, 300);
  };

  const handleZoomOut = () => {
    if (!fgRef.current) return;
    fgRef.current.zoom(fgRef.current.zoom() / 1.2, 300);
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        cooldownTicks={100}
        width={containerRef.current?.clientWidth || 800}
        height={containerRef.current?.clientHeight || 600}
        nodeCanvasObjectMode={() => "after"}
        nodeCanvasObject={(node, ctx, globalScale) => {
          const size = 8;
          const label = node.name;

          ctx.fillStyle = getColor(node);
          ctx.beginPath();
          ctx.rect(node.x - size / 2, node.y - size / 2, size, size);
          ctx.fill();

          if (matchedNodes.some((m) => m.id === node.id)) {
            ctx.strokeStyle = "#1644ddff";
            ctx.lineWidth = 4 / globalScale;
            ctx.strokeRect(
              node.x - size / 2 - 1,
              node.y - size / 2 - 1,
              size + 2,
              size + 2
            );
          }

          const fontSize = 11 / globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          ctx.fillStyle = darkMode ? "#ffffff" : "#000000";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(label, node.x, node.y - size / 2 - 2);
        }}
        nodePointerAreaPaint={(node, color, ctx) => {
          const size = 8;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.rect(node.x - size / 2, node.y - size / 2, size, size);
          ctx.fill();
        }}
        onNodeHover={setHoverNode}
        linkColor={() => "#4caf50"}
        linkDirectionalArrowLength={3}
        linkDirectionalArrowRelPos={1}
      />

      {/* Zoom Controls */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 16,
          display: "flex",
          gap: "8px",
          zIndex: 10,
        }}
      >
        <IconButton
          size="small"
          onClick={handleZoomIn}
          sx={{ width: 28, height: 28 }}
        >
          ➕
        </IconButton>
        <IconButton
          size="small"
          onClick={handleZoomOut}
          sx={{ width: 28, height: 28 }}
        >
          ➖
        </IconButton>
      </Box>

      {/* Node Tooltip */}
      <div style={{ position: "absolute", top: 0, right: 0 }}>
        <NodeTooltip node={hoverNode} />
      </div>
    </div>
  );
}
